import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShieldX, RefreshCw, Mail, AlertTriangle, Ban, Clock, ArrowRight, Lock } from "lucide-react";
import { auth, userSettings } from "../../services/api/client";

// ─── Status configurations ─────────────────────────────────────────────────
const STATUS_CONFIGS = {
  expired: {
    icon: Clock,
    iconColor: "text-yellow-400",
    borderColor: "border-yellow-400/30",
    bgGlow: "from-yellow-900/20",
    badge: "bg-yellow-500/10 text-yellow-400 border border-yellow-500/20",
    badgeText: "LICENCE EXPIRÉE",
    title: "Votre licence a expiré",
    subtitle: "L'accès à la plateforme est suspendu en attente de renouvellement.",
    actions: [
      { label: "Renouveler ma licence", href: "mailto:contact@sylink.fr?subject=Renouvellement licence SYLink", primary: true },
      { label: "Accéder aux paramètres", href: "/account?tab=settings", primary: false },
    ],
  },
  revoked: {
    icon: Ban,
    iconColor: "text-red-400",
    borderColor: "border-red-400/30",
    bgGlow: "from-red-900/20",
    badge: "bg-red-500/10 text-red-400 border border-red-500/20",
    badgeText: "LICENCE RÉVOQUÉE",
    title: "Votre licence a été révoquée",
    subtitle: "L'accès à la plateforme a été suspendu par SYLink Security. Contactez votre commercial.",
    actions: [
      { label: "Contacter SYLink", href: "mailto:contact@sylink.fr?subject=Révocation licence SYLink", primary: true },
    ],
  },
  cancelled: {
    icon: ShieldX,
    iconColor: "text-orange-400",
    borderColor: "border-orange-400/30",
    bgGlow: "from-orange-900/20",
    badge: "bg-orange-500/10 text-orange-400 border border-orange-500/20",
    badgeText: "LICENCE RÉSILIÉE",
    title: "Votre licence a été résiliée",
    subtitle: "Votre abonnement a été résilié. Vos données sont conservées 30 jours.",
    actions: [
      { label: "Souscrire un nouvel abonnement", href: "mailto:contact@sylink.fr?subject=Nouveau abonnement SYLink", primary: true },
      { label: "Exporter mes données", href: "/account?tab=reports", primary: false },
    ],
  },
  no_license: {
    icon: Lock,
    iconColor: "text-gray-400",
    borderColor: "border-gray-400/30",
    bgGlow: "from-gray-900/20",
    badge: "bg-gray-500/10 text-gray-400 border border-gray-500/20",
    badgeText: "AUCUNE LICENCE",
    title: "Aucune licence active",
    subtitle: "Activez une licence pour accéder à la plateforme SYLink Pentest.",
    actions: [
      { label: "Activer ma licence", href: "/account?tab=settings", primary: true },
      { label: "Contacter SYLink", href: "mailto:contact@sylink.fr?subject=Licence SYLink", primary: false },
    ],
  },
};

// ─── Animated grid background ─────────────────────────────────────────────
const GridBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div
      className="absolute inset-0 opacity-10"
      style={{
        backgroundImage: `
          linear-gradient(to right, #00ff88 1px, transparent 1px),
          linear-gradient(to bottom, #00ff88 1px, transparent 1px)
        `,
        backgroundSize: "40px 40px",
      }}
    />
    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-brand-black/50 to-brand-black" />
  </div>
);

// ─── Main page ─────────────────────────────────────────────────────────────
export default function LicenseExpiredPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Determine status from query param or auto-detect
  const statusParam = searchParams.get("reason") || "expired";
  const config = STATUS_CONFIGS[statusParam] || STATUS_CONFIGS.expired;
  const IconComponent = config.icon;

  useEffect(() => {
    const loadInfo = async () => {
      try {
        const [userData, settingsData] = await Promise.allSettled([
          auth.getCurrentUser(),
          userSettings.get(),
        ]);

        if (userData.status === "fulfilled") {
          const u = userData.value;
          setUserEmail(u.email || "");
          setIsSuperAdmin(u.role === "super_admin" || u.is_superuser);
        }

        if (settingsData.status === "fulfilled") {
          const lic = settingsData.value?.license_settings || {};
          setLicenseInfo(lic);
        }
      } catch (_) {
        // silently ignore
      } finally {
        setLoading(false);
      }
    };
    loadInfo();
  }, []);

  // Superadmin bypass — redirect back to dashboard
  useEffect(() => {
    if (!loading && isSuperAdmin) {
      navigate("/dashboard", { replace: true });
    }
  }, [loading, isSuperAdmin, navigate]);

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    } catch {
      return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-black">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-green" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-black flex items-center justify-center p-4 relative overflow-hidden">
      <GridBackground />

      {/* Main card */}
      <div className="relative z-10 max-w-2xl w-full">

        {/* Glow effect behind card */}
        <div className={`absolute -inset-px rounded-2xl bg-gradient-to-b ${config.bgGlow} to-transparent blur-xl`} />

        <div className={`relative bg-gray-900/80 backdrop-blur-xl rounded-2xl border ${config.borderColor} p-8 shadow-2xl`}>

          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img src="/logo.png" alt="SYLink" className="h-8 opacity-60" />
          </div>

          {/* Status badge */}
          <div className="flex justify-center mb-6">
            <span className={`px-3 py-1 rounded-full text-xs font-bold tracking-widest ${config.badge}`}>
              {config.badgeText}
            </span>
          </div>

          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className={`w-20 h-20 rounded-full bg-gray-800/60 border ${config.borderColor} flex items-center justify-center`}>
              <IconComponent className={`w-10 h-10 ${config.iconColor}`} />
            </div>
          </div>

          {/* Title & subtitle */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white mb-3">{config.title}</h1>
            <p className="text-gray-400 text-sm leading-relaxed max-w-md mx-auto">
              {config.subtitle}
            </p>
          </div>

          {/* License info block */}
          {licenseInfo && (licenseInfo.license_id || licenseInfo.expires_at) && (
            <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 p-4 mb-6 space-y-2">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Informations licence</p>
              {licenseInfo.license_id && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Référence</span>
                  <span className="text-white font-mono">{licenseInfo.license_id}</span>
                </div>
              )}
              {licenseInfo.tier && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Tier</span>
                  <span className="text-white">{licenseInfo.tier}</span>
                </div>
              )}
              {licenseInfo.expires_at && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Expirée le</span>
                  <span className={`font-medium ${statusParam === "expired" ? "text-yellow-400" : "text-gray-300"}`}>
                    {formatDate(licenseInfo.expires_at)}
                  </span>
                </div>
              )}
              {licenseInfo.customer?.account_company && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Titulaire</span>
                  <span className="text-white">{licenseInfo.customer.account_company}</span>
                </div>
              )}
            </div>
          )}

          {/* Warning message */}
          <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-xl p-4 mb-8 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-300 leading-relaxed">
              {statusParam === "expired" && (
                <>
                  Vos données sont préservées. Renouvelez votre licence pour restaurer l&apos;accès complet à la plateforme.
                  {licenseInfo?.customer?.account_email && (
                    <span> Un email de rappel a été envoyé à <strong className="text-white">{licenseInfo.customer.account_email}</strong>.</span>
                  )}
                </>
              )}
              {statusParam === "revoked" && (
                "L'accès a été révoqué suite à une décision administrative. Contactez immédiatement votre interlocuteur SYLink."
              )}
              {statusParam === "cancelled" && (
                "Votre abonnement est résilié. Vous disposez de 30 jours pour exporter vos données avant suppression définitive."
              )}
              {statusParam === "no_license" && (
                "Accédez aux paramètres de votre compte pour saisir votre clé de licence et activer la plateforme."
              )}
            </div>
          </div>

          {/* CTA buttons */}
          <div className="space-y-3">
            {config.actions.map((action, idx) => (
              action.href.startsWith("mailto") ? (
                <a
                  key={idx}
                  href={action.href}
                  className={`flex items-center justify-center gap-2 w-full px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
                    action.primary
                      ? "bg-brand-green text-black hover:bg-brand-green/90 hover:shadow-lg hover:shadow-brand-green/20"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white border border-gray-700"
                  }`}
                >
                  {action.primary ? <RefreshCw className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                  {action.label}
                  <ArrowRight className="w-4 h-4" />
                </a>
              ) : (
                <button
                  key={idx}
                  onClick={() => navigate(action.href)}
                  className={`flex items-center justify-center gap-2 w-full px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
                    action.primary
                      ? "bg-brand-green text-black hover:bg-brand-green/90 hover:shadow-lg hover:shadow-brand-green/20"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white border border-gray-700"
                  }`}
                >
                  {action.primary ? <RefreshCw className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                  {action.label}
                </button>
              )
            ))}
          </div>

          {/* Contact footer */}
          <div className="mt-8 pt-6 border-t border-gray-800 text-center">
            <p className="text-xs text-gray-500">
              Besoin d&apos;aide ?{" "}
              <a
                href="mailto:contact@sylink.fr"
                className="text-brand-green hover:underline"
              >
                contact@sylink.fr
              </a>{" "}
              — SYLink Security, 7j/7
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
